import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft,
    RefreshCw,
    Pencil,
    Loader2,
    ShieldCheck,
    FileText,
    Download,
    Hash,
    Calendar,
    MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { crud } from "../lib/api";
import { resolveEntity } from "../lib/entities";
import { getTableConfig } from "../lib/entityTableConfig";
import {
    formatValue,
    isStatusKey,
    StatusBadge,
    humanizeKey,
    isBooleanField,
} from "../lib/utils-fields";
import EvrakSection from "../components/EvrakSection";
import SigortaVekaletBridge from "../components/SigortaVekaletBridge";
import DegerKaybiParcalariSection from "../components/DegerKaybiParcalariSection";
import VekaletDosyaSection from "../components/VekaletDosyaSection";
import WorkflowPanel from "../components/WorkflowPanel";
import DosyaDurumuWorkflowPanel from "../components/DosyaDurumuWorkflowPanel";
import DosyaYorumlariSection from "../components/DosyaYorumlariSection";
import SigortaDosyaEpostaSection from "../components/SigortaDosyaEpostaSection";
import EvrakTalepMailPanel from "../components/EvrakTalepMailPanel";
import TahkimDosyaDetailSection from "../components/TahkimDosyaDetailSection";
import IcraDosyaDetailSection from "../components/IcraDosyaDetailSection";
import {
    SigortaSummaryHero,
    DetailTabBar,
    DetailFieldGrid,
} from "../components/EntityDetailTabs";
import QuickMessageModal from "../components/QuickMessageModal";
import WhatsAppGecmisiSection from "../components/whatsapp/WhatsAppGecmisiSection";
import EntityForm from "../components/EntityForm";
import DosyaIntelPanel from "../components/ai/DosyaIntelPanel";
import AktueryaDosyaPanel from "../components/aktuerya/AktueryaDosyaPanel";
import KusurDosyaPanel from "../components/kusur/KusurDosyaPanel";
import { setAkilContext } from "../lib/akilContext";
import { normalizeEntityPayload } from "../lib/panelPayloadNormalize";

const HIDE_DETAIL = new Set([
    "id",
    "pk",
    "sifre",
    "password",
    "log",
    "evraklar",
    "muvekkiller_detay",
    "araci_odemeleri",
    "toplam_araciya_odenen",
    "toplam_odenen_hakedis",
    "toplam_odendi_hakedis",
    "toplam_bekleyen_hakedis",
    "dosya_durumu_guncelleme",
    "eksik_evrak_mail_gonderildi",
    "sure_uyarisi_olusturuldu",
    "mail_gonder",
    "eksik_evrak_sayisi",
]);

function pickDisplayValue(row, key) {
    if (!row) return null;
    if (key === "aracilar") {
        const label = row.aracilar_label;
        if (label !== undefined && label !== null && String(label).trim() !== "") {
            return label;
        }
    }
    for (const suffix of ["_display", "_label", "_ad_soyad"]) {
        const dk = `${key}${suffix}`;
        if (row[dk] !== undefined && row[dk] !== null && row[dk] !== "")
            return row[dk];
    }
    return row[key];
}

function DetailField({ label, fieldKey, value, fieldType, highlight }) {
    if (value === null || value === undefined || value === "") return null;
    const show =
        isStatusKey(fieldKey) && !isBooleanField(fieldKey, fieldType) ? (
            <StatusBadge value={value} />
        ) : (
            formatValue(value, fieldType, fieldKey)
        );
    return (
        <div
            className={`rounded-xl border px-4 py-3.5 transition-colors ${
                highlight
                    ? "border-[#D4AF37]/35 bg-gradient-to-br from-[#D4AF37]/8 to-[#0B0F19]/80"
                    : "border-white/10 bg-[#0B0F19]/55 hover:border-white/15"
            }`}
        >
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#D4AF37]/75 mb-1.5">
                {label}
            </div>
            <div
                className={`break-words ${
                    highlight
                        ? "text-base font-medium text-[#F3E5AB]"
                        : "text-sm text-slate-100"
                }`}
            >
                {show}
            </div>
        </div>
    );
}

export default function EntityDetailPage() {
    const { entityKey, id } = useParams();
    const def = useMemo(() => resolveEntity(entityKey || ""), [entityKey]);
    const tableConfig = useMemo(
        () => getTableConfig(entityKey, def),
        [entityKey, def],
    );
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [messageOpen, setMessageOpen] = useState(false);
    const [sigortaTab, setSigortaTab] = useState("ozet");
    const [formOpen, setFormOpen] = useState(false);

    const load = useCallback(async () => {
        if (!def?.resource) return;
        setLoading(true);
        try {
            const data = await crud.retrieve(def.resource, id);
            setRow(data);
        } catch {
            toast.error("Kayıt detayı yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [def, id]);

    useEffect(() => {
        load();
    }, [load]);

    const openEdit = useCallback(() => {
        if (!row) return;
        setFormOpen(true);
    }, [row]);

    const closeForm = useCallback(() => setFormOpen(false), []);

    const submitForm = useCallback(
        async (payload, opts = {}) => {
            if (!def?.resource || !id) return;
            const body = normalizeEntityPayload(entityKey, payload);
            await crud.update(def.resource, id, body, { force: opts.force });
            toast.success(`${def.label} güncellendi`);
            setFormOpen(false);
            load();
        },
        [def, entityKey, id, load],
    );

    useEffect(() => {
        if (!row || !entityKey) return undefined;
        if (entityKey === "sigorta-dosyalari") {
            const mLabel =
                row.muvekkil_display ||
                row.muvekkil_ad_soyad ||
                (row.muvekkil && typeof row.muvekkil === "object"
                    ? `${row.muvekkil.ad || ""} ${row.muvekkil.soyad || ""}`.trim()
                    : row.muvekkil);
            setAkilContext({
                sigorta_dosyasi_id: row.id,
                dosya_no: row.dosya_no,
                plaka: row.plaka,
                muvekkil_label: mLabel,
                muvekkil_id: row.muvekkil?.id || row.muvekkil,
            });
        } else if (entityKey === "muvekkiller") {
            const tc = row.tc_kimlik_no ? ` (${row.tc_kimlik_no})` : "";
            setAkilContext({
                muvekkil_id: row.id,
                muvekkil_label: `${row.ad || ""} ${row.soyad || ""}${tc}`.trim(),
            });
        } else if (entityKey === "aracilar") {
            setAkilContext({
                araci_id: row.id,
                araci_label: row.ad || row.label || `Aracı #${row.id}`,
                module: "aracilar",
                module_label: "Aracılar",
            });
        } else {
            setAkilContext(null);
        }
        return () => setAkilContext(null);
    }, [row, entityKey]);

    const fieldGroups = useMemo(() => {
        const labels = tableConfig?.labels || {};
        const rawDeclared = def?.fields || [];
        const declared = [];
        for (const f of rawDeclared) {
            if (f.__groups) {
                for (const g of f.__groups) {
                    if (g.fields) {
                        for (const gf of g.fields) {
                            declared.push({
                                ...gf,
                                group: g.title || gf.group,
                            });
                        }
                    }
                }
            } else {
                declared.push(f);
            }
        }
        const byName = new Map(declared.map((f) => [f.name, f]));
        const groups = new Map();

        const addField = (name, label, type) => {
            if (!name) return;
            if (HIDE_DETAIL.has(name)) return;
            if (typeof name === "string" && name.endsWith("_id")) return;
            if (isReadOnlyDuplicate(name, row)) return;
            const val = pickDisplayValue(row, name);
            if (val === null || val === undefined || val === "") return;
            if (Array.isArray(val) && val.length === 0) return;
            const fld = byName.get(name);
            const groupTitle = fld?.group || "Genel Bilgiler";
            if (!groups.has(groupTitle)) groups.set(groupTitle, []);
            groups.get(groupTitle).push({
                name,
                label: labels[name] || fld?.label || humanizeKey(name),
                type: fld?.type,
                value: val,
            });
        };

        const declaredNames = new Set();

        if (declared.length) {
            for (const fld of declared) {
                declaredNames.add(fld.name);
                addField(fld.name, fld.label, fld.type);
            }
        }

        // Liste/detay API'sinde olup formda tanımlı olmayan özet alanlar (ör. deger-kaybi dosya_no)
        for (const name of tableConfig?.columns || []) {
            if (declaredNames.has(name)) continue;
            addField(name, labels[name] || humanizeKey(name));
        }

        if (!declared.length && row) {
            for (const k of Object.keys(row)) {
                if (k.endsWith("_display") || k.endsWith("_label")) continue;
                addField(k, humanizeKey(k));
            }
        }

        return Array.from(groups.entries());
    }, [def, row, tableConfig]);

    function isReadOnlyDuplicate(name, data) {
        if (!data) return false;
        const base = pickDisplayValue(data, name);
        if (base === null || base === undefined) return true;
        return false;
    }

    const title =
        row?.dosya_no ||
        row?.ad_soyad ||
        [row?.ad, row?.soyad].filter(Boolean).join(" ") ||
        row?.fatura_no ||
        row?.baslik ||
        row?.konu ||
        row?.servis_ismi ||
        def?.label ||
        "";

    const highlightKeys = useMemo(() => {
        const cols = tableConfig?.columns || [];
        return new Set(cols.slice(0, 4));
    }, [tableConfig]);

    const updatedAt =
        row?.guncelleme_tarihi ||
        row?.updated_at ||
        row?.olusturma_tarihi ||
        row?.created_at ||
        null;

    async function downloadPdf() {
        if (!def?.resource || id == null) return;
        setPdfBusy(true);
        try {
            await crud.exportDetailPdf(def.resource, id);
            toast.success("Kurumsal PDF açıldı");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "PDF oluşturulamadı.",
            );
        } finally {
            setPdfBusy(false);
        }
    }

    if (!def) {
        return (
            <div className="p-12 text-center text-slate-500">
                Modül bulunamadı.
            </div>
        );
    }

    return (
        <div className="max-w-screen-2xl mx-auto space-y-6 pb-16">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Link
                        to={`/e/${entityKey}`}
                        className="h-11 w-11 rounded-lg border border-white/10 bg-[#121A2F] flex items-center justify-center text-slate-400 hover:text-[#F3E5AB] hover:border-[#D4AF37]/40 transition"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Link>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] flex items-center gap-2">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            {def.title} · Detay
                        </div>
                        <h1 className="font-display text-3xl text-white mt-1">
                            {title}
                        </h1>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="h-10 px-4 rounded-lg border border-white/10 bg-[#0B0F19]/60 text-slate-300 hover:text-white text-sm inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4" />
                        )}
                        Yenile
                    </button>
                    {!def?.readOnlyCrud && (
                        <button
                            type="button"
                            onClick={openEdit}
                            disabled={loading || !row}
                            className="btn-gold h-10 px-5 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            <Pencil className="h-4 w-4" />
                            Düzenle
                        </button>
                    )}
                    {entityKey === "sigorta-dosyalari" && row && (
                        <button
                            type="button"
                            onClick={() => setMessageOpen(true)}
                            className="h-10 px-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-sm inline-flex items-center gap-2"
                        >
                            <MessageCircle className="h-4 w-4" />
                            SMS / WhatsApp
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={downloadPdf}
                        disabled={loading || pdfBusy || !row}
                        data-testid="detail-pdf-button"
                        className="h-10 px-4 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#F3E5AB] text-sm inline-flex items-center gap-2 hover:bg-[#D4AF37]/20 disabled:opacity-50"
                        title="MBS kurumsal şablon — logo ve alan tablosu"
                    >
                        {pdfBusy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Download className="h-4 w-4" />
                        )}
                        PDF İndir
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="h-64 flex items-center justify-center rounded-2xl border border-white/10 bg-[#121A2F]">
                    <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
                </div>
            ) : (
                <>
                    {entityKey === "sigorta-dosyalari" && row && (
                        <>
                            <SigortaSummaryHero row={row} />
                            <DetailTabBar
                                active={sigortaTab}
                                onChange={setSigortaTab}
                                tabs={[
                                    { id: "ozet", label: "Özet" },
                                    { id: "bilgiler", label: "Kayıt Bilgileri" },
                                    { id: "islem", label: "İşlem & Notlar" },
                                    { id: "evrak", label: "Evrak" },
                                    { id: "evrak_talep", label: "Evrak Talep Mail" },
                                    { id: "whatsapp", label: "WhatsApp geçmişi" },
                                    { id: "eposta", label: "E-postalar" },
                                ]}
                            />
                        </>
                    )}

                    {entityKey === "sigorta-dosyalari" && sigortaTab === "ozet" && row && (
                        <>
                            <DosyaIntelPanel dosyaId={Number(id)} />
                            <AktueryaDosyaPanel dosyaId={Number(id)} />
                            <KusurDosyaPanel dosyaId={Number(id)} />
                            <DetailFieldGrid
                                fieldGroups={fieldGroups.slice(0, 2)}
                                highlightKeys={highlightKeys}
                                compact
                            />
                        </>
                    )}

                    {entityKey === "sigorta-dosyalari" && sigortaTab === "bilgiler" && (
                        <DetailFieldGrid fieldGroups={fieldGroups} highlightKeys={highlightKeys} />
                    )}

                    {entityKey === "sigorta-dosyalari" && sigortaTab === "islem" && (
                        <>
                            {row?.dosya_durumu && (
                                <WorkflowPanel
                                    dosyaId={Number(id)}
                                    currentStatus={row.dosya_durumu}
                                    onTransition={load}
                                />
                            )}
                            {row && (
                                <DosyaDurumuWorkflowPanel
                                    dosyaId={Number(id)}
                                    dosyaTipi={row.dosya_tipi || "TAHKIM"}
                                    currentDurum={row.dosya_durumu || "YENI"}
                                    dosyaDurumuLabel={row.dosya_durumu_display || row.dosya_durumu}
                                    gun={
                                        row.dosya_durumu_guncelleme
                                            ? Math.max(
                                                  0,
                                                  Math.floor(
                                                      (Date.now() -
                                                          new Date(
                                                              row.dosya_durumu_guncelleme,
                                                          ).getTime()) /
                                                          86400000,
                                                  ),
                                              )
                                            : 0
                                    }
                                    onTransition={load}
                                />
                            )}
                            <DosyaYorumlariSection dosyaId={Number(id)} />
                        </>
                    )}

                    {entityKey === "sigorta-dosyalari" && sigortaTab === "evrak" && (
                        <>
                            <SigortaVekaletBridge dosyaId={Number(id)} />
                            <EvrakSection parentId={id} relationField="sigorta_dosyasi" />
                        </>
                    )}

                    {entityKey === "sigorta-dosyalari" && sigortaTab === "evrak_talep" && row && (
                        <EvrakTalepMailPanel dosyaId={Number(id)} dosyaNo={row.dosya_no} />
                    )}

                    {entityKey === "sigorta-dosyalari" && sigortaTab === "whatsapp" && (
                        <WhatsAppGecmisiSection dosyaId={Number(id)} />
                    )}

                    {entityKey === "sigorta-dosyalari" && sigortaTab === "eposta" && row && (
                        <SigortaDosyaEpostaSection dosyaId={Number(id)} dosyaNo={row.dosya_no} />
                    )}

                    {entityKey === "tahkim-dosyalari" && row && (
                        <TahkimDosyaDetailSection row={row} onRefresh={load} />
                    )}

                    {entityKey === "icralar" && row && (
                        <IcraDosyaDetailSection row={row} onRefresh={load} />
                    )}

                    {entityKey !== "sigorta-dosyalari" &&
                        entityKey !== "tahkim-dosyalari" &&
                        entityKey !== "icralar" &&
                        fieldGroups.map(([groupTitle, fields]) => (
                            <section
                                key={groupTitle}
                                className="rounded-2xl border border-white/10 bg-[#121A2F]/90 overflow-hidden shadow-lg shadow-black/15"
                            >
                                <div className="px-5 py-3.5 border-b border-white/10 bg-gradient-to-r from-[#D4AF37]/12 via-[#D4AF37]/5 to-transparent flex items-center gap-2">
                                    <FileText className="h-3.5 w-3.5 text-[#D4AF37]" />
                                    <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
                                        {groupTitle}
                                    </h2>
                                </div>
                                <div className="p-4">
                                    <DetailFieldGrid
                                        fieldGroups={[[groupTitle, fields]]}
                                        highlightKeys={highlightKeys}
                                    />
                                </div>
                            </section>
                        ))}

                    {entityKey === "deger-kaybi" && (
                        <DegerKaybiParcalariSection parentId={id} />
                    )}

                    {entityKey === "vekaletler" && (
                        <VekaletDosyaSection
                            parentId={id}
                            onParentUpdated={() => load()}
                        />
                    )}

                    {entityKey === "aracilar" && (
                        <AraciPortfolioSection row={row} onRefresh={load} />
                    )}
                </>
            )}

            <QuickMessageModal
                open={messageOpen}
                onClose={() => setMessageOpen(false)}
                dosyaId={
                    entityKey === "sigorta-dosyalari" ? Number(id) : null
                }
                dosyaNo={row?.dosya_no}
            />

            {def && !def.readOnlyCrud && (
                <EntityForm
                    open={formOpen}
                    title={`${def.label} Düzenle`}
                    fields={def.fields || []}
                    initial={row}
                    resource={def.resource}
                    schema={def.schema}
                    formFieldsStrict={def.formFieldsStrict}
                    onClose={closeForm}
                    onSubmit={submitForm}
                />
            )}
        </div>
    );
}

function formatMoney(val) {
    if (val === null || val === undefined || val === "") return "—";
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 2,
    }).format(n);
}

function AraciPortfolioSection({ row, onRefresh }) {
    const odemeler = Array.isArray(row?.araci_odemeleri)
        ? row.araci_odemeleri
        : [];
    const muvekkiller = Array.isArray(row?.muvekkiller_detay)
        ? row.muvekkiller_detay
        : [];
    const [busyId, setBusyId] = useState(null);

    const toplamMuvekkil =
        row?.toplam_araciya_odenen ??
        muvekkiller.reduce(
            (s, m) => s + Number(m.araciya_odenen_miktar || 0),
            0,
        );
    const toplamOdendi =
        row?.toplam_odendi_hakedis ??
        muvekkiller.reduce(
            (s, m) =>
                s +
                (m.araci_hakedisi_odendi
                    ? Number(m.araciya_odenen_miktar || 0)
                    : 0),
            0,
        );
    const toplamBekleyen =
        row?.toplam_bekleyen_hakedis ??
        muvekkiller.reduce(
            (s, m) =>
                s +
                (!m.araci_hakedisi_odendi
                    ? Number(m.araciya_odenen_miktar || 0)
                    : 0),
            0,
        );

    const toggleOdendi = async (muvekkil) => {
        if (!muvekkil?.id || busyId) return;
        const next = !muvekkil.araci_hakedisi_odendi;
        setBusyId(muvekkil.id);
        try {
            await crud.update("muvekkiller", muvekkil.id, {
                araci_hakedisi_odendi: next,
            });
            toast.success(
                next
                    ? "Hakediş ödendi olarak işaretlendi"
                    : "Ödeme bekliyor olarak işaretlendi",
            );
            onRefresh?.();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.response?.data?.araci_hakedisi_odendi?.[0] ||
                    "Durum güncellenemedi",
            );
        } finally {
            setBusyId(null);
        }
    };

    return (
        <section className="rounded-lg border border-white/10 bg-[#121A2F] overflow-hidden">
            <div className="px-5 py-3 border-b border-white/10 bg-gradient-to-r from-[#D4AF37]/10 to-transparent flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
                    Aracı hakediş ödemeleri
                </h2>
                <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-wider">
                    <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-emerald-300">
                        Ödendi: {formatMoney(toplamOdendi)}
                    </span>
                    <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-amber-200">
                        Bekliyor: {formatMoney(toplamBekleyen)}
                    </span>
                </div>
            </div>
            {muvekkiller.length === 0 ? (
                <p className="px-5 py-6 text-sm text-slate-400">
                    Bu aracıya bağlı müvekkil kaydı yok.
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="text-left border-b border-white/10 text-[10px] uppercase tracking-[0.14em] text-slate-500">
                                <th className="px-5 py-3">Müvekkil</th>
                                <th className="px-5 py-3">Telefon</th>
                                <th className="px-5 py-3 text-right">
                                    Hakediş tutarı
                                </th>
                                <th className="px-5 py-3 text-center">
                                    Ödeme durumu
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {muvekkiller.map((m) => (
                                <tr
                                    key={m.id}
                                    className="border-b border-white/5"
                                >
                                    <td className="px-5 py-3 text-slate-100">
                                        <Link
                                            to={`/e/muvekkiller/${m.id}`}
                                            className="hover:text-[#D4AF37] transition-colors"
                                        >
                                            {m.ad_soyad || `#${m.id}`}
                                        </Link>
                                    </td>
                                    <td className="px-5 py-3 text-slate-400">
                                        {m.telefon || "—"}
                                    </td>
                                    <td className="px-5 py-3 text-right font-mono text-[#F3E5AB]">
                                        {formatMoney(m.araciya_odenen_miktar)}
                                    </td>
                                    <td className="px-5 py-3">
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                type="button"
                                                disabled={busyId === m.id}
                                                onClick={() => toggleOdendi(m)}
                                                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                                                    m.araci_hakedisi_odendi
                                                        ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                                                        : "border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
                                                }`}
                                            >
                                                {busyId === m.id ? (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                ) : null}
                                                {m.araci_hakedisi_odendi
                                                    ? "Ödendi"
                                                    : "Ödenmedi"}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="bg-[#0B0F19]/60">
                                <td
                                    colSpan={2}
                                    className="px-5 py-3 text-xs uppercase tracking-wider text-slate-500"
                                >
                                    Toplam hakediş
                                </td>
                                <td className="px-5 py-3 text-right font-semibold text-[#D4AF37]">
                                    {formatMoney(toplamMuvekkil)}
                                </td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {odemeler.length > 0 ? (
                <div className="overflow-x-auto border-t border-white/10">
                    <div className="px-5 py-2 text-[10px] uppercase tracking-[0.14em] text-slate-500">
                        Dosya bazlı aracı ödemeleri
                    </div>
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="text-left border-b border-white/10 text-[10px] uppercase tracking-[0.14em] text-slate-500">
                                <th className="px-5 py-3">Dosya</th>
                                <th className="px-5 py-3">Tarih</th>
                                <th className="px-5 py-3 text-right">Tutar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {odemeler.map((o) => (
                                <tr
                                    key={o.id}
                                    className="border-b border-white/5"
                                >
                                    <td className="px-5 py-3 text-slate-100">
                                        {o.dosya_no || o.aciklama || "—"}
                                    </td>
                                    <td className="px-5 py-3 text-slate-400">
                                        {o.tarih
                                            ? new Date(
                                                  o.tarih,
                                              ).toLocaleDateString("tr-TR")
                                            : "—"}
                                    </td>
                                    <td className="px-5 py-3 text-right font-mono text-emerald-300">
                                        {formatMoney(o.tutar)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </section>
    );
}
